import { useContext, useEffect, useMemo } from "react";
import { Loadable, LoadableRequestOptions } from "./loadable";

import { useDidUpdateEffect } from "../util";
import { ApiServiceContext } from "../services/api";
import { AuthServiceContext } from "../services/auth";
import { I18nServiceContext } from "../services/i18n";

export interface LoaderOptions<T> {
  onLoaded?: (value: T) => void;
  lazy?: boolean;
  skipRefreshOnMount?: boolean;
}

export function useLoader<T extends {} | undefined>(
  loadable: Loadable<T>,
  { onLoaded, lazy, skipRefreshOnMount }: LoaderOptions<T> = {}
) {
  const apiService = useContext(ApiServiceContext);
  const authService = useContext(AuthServiceContext);
  const i18nService = useContext(I18nServiceContext);

  // On lazy render synchronously reset the error
  useMemo(() => {
    if (lazy) loadable.setError(undefined);
  }, []);

  async function refresh(options: LoadableRequestOptions = {}) {
    if (!loadable) return;

    try {
      await loadable.refresh(options);
      onLoaded?.(loadable.value as T);
    } catch (e) {
      if (!lazy) throw e;
    }
  }

  function retry() {
    const status = loadable.error?.response?.status;

    if (status === 401) {
      // Wipe out access token because access token may be expired
      // (unauthorized exception)
      authService.setAccessToken(undefined);
    }

    refresh({ ...loadable.options.defaultRequestOptions });
  }

  // When focused and data is stale (expired) reload data of this loader
  function reloadIfStale() {
    if (loadable?.isStale) refresh({ reloadData: true });
  }

  // Refresh on mount
  useEffect(() => {
    if (skipRefreshOnMount) return;
    refresh({ ...loadable.options.defaultRequestOptions });
  }, [loadable]);

  // Refresh when language changes
  useDidUpdateEffect(() => {
    refresh({ ...loadable.options.defaultRequestOptions, reloadData: true });
  }, [loadable?.options.translatedContent && i18nService.preferredLanguage]);

  // Refresh when API base path changed
  useDidUpdateEffect(() => {
    refresh({ ...loadable.options.defaultRequestOptions, reset: true });
  }, [apiService.basePath]);

  return { loadable, retry, reloadIfStale };
}
